import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

/** The signed-in user, mirrored from `GET /auth/me`. */
export type Session = {
  userId: string;
  email: string;
  name: string | null;
};

type AuthState = {
  /** null = resolved-and-signed-out; undefined never leaks (loading covers it). */
  session: Session | null;
  loading: boolean;
  error: string | null;
  /** Send the browser to the WorkOS hosted login. */
  signIn: () => void;
  signOut: () => Promise<void>;
};

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch("/auth/me", { credentials: "include" });
        if (!alive) return;
        if (res.status === 401) {
          setSession(null);
          return;
        }
        if (!res.ok) throw new Error(`Failed to load session (${res.status})`);
        const body = (await res.json()) as Session;
        if (alive) setSession(body);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const signIn = () => {
    window.location.href = "/auth/login";
  };

  const signOut = async () => {
    await fetch("/auth/logout", { method: "POST", credentials: "include" });
    window.location.href = "/";
  };

  return (
    <AuthCtx.Provider value={{ session, loading, error, signIn, signOut }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
